import React, { useCallback, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import Button from '../../../../../components/button';
import { useToasts } from '../../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import {
  useEditingDisabled,
  useServerFeature,
} from '../../../../../hooks/useServerFeature';
import { formatDateShort } from '../../../../../utils/dateUtils';
import RedirectToHome from '../../../components/RedirectToHome';
import { useUserInfo } from '../../../context/UserInfo';
import {
  useProfileEducationExperienceQuery,
  useDeleteMyEducationMutation,
  useDeleteMyExperienceMutation,
  ProfileEducationExperienceQuery,
} from '../../../__generated__/graphql';
import Loading from '../PersonalData/Loading';
import {
  EducationSaveModal,
  EducationRecord,
} from '../Education/EducationSaveModal';
import {
  ExperienceSaveModal,
  ExperienceRecord,
} from '../Experience/ExperienceSaveModal';
import { DeleteConfirmation } from '../DeleteConfirmation';
import './ProfileTimeline.css';

type ModalState =
  | { type: 'education'; record: EducationRecord | null }
  | { type: 'experience'; record: ExperienceRecord | null };

type DeleteState = {
  type: 'education' | 'experience';
  id: string;
};

type GqlEducation = ProfileEducationExperienceQuery['me']['educations'][number];
type GqlExperience =
  ProfileEducationExperienceQuery['me']['experiences'][number];

const mapEducation = (edu: GqlEducation): EducationRecord => ({
  id: edu.Id,
  name: edu.Name,
  school: edu.flair__School__c ?? null,
  degree: edu.flair__Degree__c ?? null,
  location: edu.flair__Location__c ?? null,
  startDate: edu.flair__Start_Date__c ?? null,
  endDate: edu.flair__End_Date__c ?? null,
  description: edu.flair__Description__c ?? null,
});

const mapExperience = (exp: GqlExperience): ExperienceRecord => ({
  id: exp.Id,
  name: exp.Name,
  title: exp.flair__Title__c ?? null,
  companyName: exp.flair__Company_Name__c ?? null,
  companyUrl: exp.flair__Company_URL__c ?? null,
  companyIndustryId: exp.flair__Company_Industry__c ?? null,
  employmentTypeId: exp.flair__Employment_Type__c ?? null,
  location: exp.flair__Location__c ?? null,
  startDate: exp.flair__Start_Date__c ?? null,
  endDate: exp.flair__End_Date__c ?? null,
  description: exp.flair__Description__c ?? null,
});

const formatDateRange = (
  startDate: string | null,
  endDate: string | null,
  presentLabel: string,
): string | null => {
  if (!startDate && !endDate) return null;
  const start = startDate ? formatDateShort(startDate) : '';
  const end = endDate ? formatDateShort(endDate) : presentLabel;
  return `${start} – ${end}`;
};

type TimelineItemProps = {
  title: string;
  subtitle: string | null;
  dateRange: string | null;
  location: string | null;
  description: string | null;
  onEdit?: () => void;
  onDelete?: () => void;
};

const TimelineItem: React.FC<TimelineItemProps> = ({
  title,
  subtitle,
  dateRange,
  location,
  description,
  onEdit,
  onDelete,
}) => (
  <div className="profile-timeline-item">
    <div className="profile-timeline-dot" />
    <div className="d-flex justify-content-between align-items-start">
      <div>
        <h5 className="mb-1">{title}</h5>
        {subtitle && <div className="text-muted">{subtitle}</div>}
        {dateRange && <small className="text-muted">{dateRange}</small>}
        {location && (
          <small className="text-muted d-block">
            <FlairIcon icon="location-outline" /> {location}
          </small>
        )}
        {description && <p className="mt-2 mb-0 text-muted">{description}</p>}
      </div>
      {(onEdit || onDelete) && (
        <div className="profile-timeline-actions d-flex gap-2">
          {onEdit && (
            <FlairIcon
              icon="pencil-outline"
              role="button"
              onClick={onEdit}
              className="text-muted"
            />
          )}
          {onDelete && (
            <FlairIcon
              icon="trash-outline"
              role="button"
              onClick={onDelete}
              className="text-muted"
            />
          )}
        </div>
      )}
    </div>
  </div>
);

const ProfileTab: React.FC = () => {
  const { t } = useTranslation();
  const { hasLeftCompany } = useUserInfo();
  const { addSuccess } = useToasts();
  const errorHandler = useMutationErrorHandler();

  const isProfileVisible = useServerFeature('Employee_Profile');
  const isEducationEditingDisabled = useEditingDisabled('Education');
  const isExperienceEditingDisabled = useEditingDisabled('Experience');

  const { data, loading, refetch } = useProfileEducationExperienceQuery({
    skip: !isProfileVisible,
  });

  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [deleteState, setDeleteState] = useState<DeleteState | null>(null);

  const [deleteEducation, { loading: deleteEducationLoading }] =
    useDeleteMyEducationMutation();
  const [deleteExperience, { loading: deleteExperienceLoading }] =
    useDeleteMyExperienceMutation();

  const handleModalSave = useCallback(() => {
    setModalState(null);
    refetch();
  }, [refetch]);

  const handleModalClose = useCallback(() => {
    setModalState(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteState) return;

    const { type, id } = deleteState;
    const deleteMutation =
      type === 'education' ? deleteEducation : deleteExperience;
    const successMessage =
      type === 'education'
        ? t('personalData.education.deleteSuccess')
        : t('personalData.experience.deleteSuccess');

    await deleteMutation({ variables: { id } })
      .then(() => {
        addSuccess(successMessage);
        setDeleteState(null);
        refetch();
      })
      .catch(errorHandler);
  }, [
    deleteState,
    deleteEducation,
    deleteExperience,
    t,
    addSuccess,
    refetch,
    errorHandler,
  ]);

  const handleDeleteClose = useCallback(() => {
    setDeleteState(null);
  }, []);

  if (!isProfileVisible) return <RedirectToHome />;

  if (loading || !data) return <Loading />;

  const sortByStartDateDesc = <T extends { startDate: string | null }>(
    a: T,
    b: T,
  ) => (b.startDate ?? '').localeCompare(a.startDate ?? '');

  const educationRecords = data.me.educations
    .map(mapEducation)
    .sort(sortByStartDateDesc);
  const experienceRecords = data.me.experiences
    .map(mapExperience)
    .sort(sortByStartDateDesc);

  const isEditable = !hasLeftCompany;
  const canEditEducation = isEditable && !isEducationEditingDisabled;
  const canEditExperience = isEditable && !isExperienceEditingDisabled;
  const presentLabel = t('personalData.profile.present');

  const renderEducationTimeline = () => {
    return (
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="card-header-title">
            {t('personalData.education.sectionTitle')}
          </h4>
          {canEditEducation && (
            <Button
              label={t('personalData.education.addButton')}
              size="sm"
              variant="outline-primary"
              onClick={() => setModalState({ type: 'education', record: null })}
            />
          )}
        </Card.Header>
        <Card.Body>
          {educationRecords.length === 0 ? (
            <p className="text-muted mb-0">
              {t('personalData.profile.noEducation')}
            </p>
          ) : (
            <div className="profile-timeline">
              {educationRecords.map((edu) => (
                <TimelineItem
                  key={edu.id}
                  title={edu.degree || edu.name}
                  subtitle={edu.school}
                  dateRange={formatDateRange(
                    edu.startDate,
                    edu.endDate,
                    presentLabel,
                  )}
                  location={edu.location}
                  description={edu.description}
                  onEdit={
                    canEditEducation
                      ? () => setModalState({ type: 'education', record: edu })
                      : undefined
                  }
                  onDelete={
                    canEditEducation
                      ? () => setDeleteState({ type: 'education', id: edu.id })
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderExperienceTimeline = () => {
    return (
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="card-header-title">
            {t('personalData.experience.sectionTitle')}
          </h4>
          {canEditExperience && (
            <Button
              label={t('personalData.experience.addButton')}
              size="sm"
              variant="outline-primary"
              onClick={() =>
                setModalState({ type: 'experience', record: null })
              }
            />
          )}
        </Card.Header>
        <Card.Body>
          {experienceRecords.length === 0 ? (
            <p className="text-muted mb-0">
              {t('personalData.profile.noExperience')}
            </p>
          ) : (
            <div className="profile-timeline">
              {experienceRecords.map((exp) => (
                <TimelineItem
                  key={exp.id}
                  title={exp.title || exp.name}
                  subtitle={exp.companyName}
                  dateRange={formatDateRange(
                    exp.startDate,
                    exp.endDate,
                    presentLabel,
                  )}
                  location={exp.location}
                  description={exp.description}
                  onEdit={
                    canEditExperience
                      ? () => setModalState({ type: 'experience', record: exp })
                      : undefined
                  }
                  onDelete={
                    canEditExperience
                      ? () => setDeleteState({ type: 'experience', id: exp.id })
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <>
      <Row>
        <Col xl={8} className="mx-auto">
          {renderExperienceTimeline()}
          {renderEducationTimeline()}
        </Col>
      </Row>

      {modalState?.type === 'education' && (
        <EducationSaveModal
          show
          onClose={handleModalClose}
          onSave={handleModalSave}
          education={modalState.record}
        />
      )}

      {modalState?.type === 'experience' && (
        <ExperienceSaveModal
          show
          onClose={handleModalClose}
          onSave={handleModalSave}
          experience={modalState.record}
        />
      )}

      <DeleteConfirmation
        visible={!!deleteState}
        onClose={handleDeleteClose}
        onSubmit={handleDeleteConfirm}
        submitInProgress={deleteEducationLoading || deleteExperienceLoading}
        message={
          deleteState?.type === 'education'
            ? t('personalData.education.deleteConfirmation')
            : t('personalData.experience.deleteConfirmation')
        }
      />
    </>
  );
};

export default ProfileTab;
